/**
 * Tag Schema Manager Component
 * 
 * UI for managing the tag taxonomy:
 * - View approved tags by category
 * - Review recently rejected tags
 * - Add new tags manually
 * - Remove tags from schema
 * - Export/import schema
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Stack,
    Badge,
    Button,
    Banner,
    TextField,
    Select,
    Modal,
    DataTable,
    Tabs,
    TextContainer,
    Spinner,
    Toast
} from '../../settings/polaris-compat';
import { db } from 'config/firebase.js';
import { collection, query, orderBy, limit, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import './TagSchemaManager.css';

const CATEGORIES = [
    { value: 'all', label: 'All Tags' },
    { value: 'category', label: 'Categories' },
    { value: 'dietary', label: 'Dietary' },
    { value: 'format', label: 'Formats' },
    { value: 'brand', label: 'Brands' },
    { value: 'attribute', label: 'Attributes' }
];

const TagSchemaManager = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [approvedTags, setApprovedTags] = useState([]);
    const [rejectedTags, setRejectedTags] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newTag, setNewTag] = useState({ tag: '', category: 'attribute' });
    const [toast, setToast] = useState(null);
    const [stats, setStats] = useState(null);

    // Load data on mount
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load approved tags
            const approvedQuery = query(
                collection(db, 'settings', 'tag_schema', 'approved_tags'),
                orderBy('approvedAt', 'desc'),
                limit(500)
            );
            const approvedSnap = await getDocs(approvedQuery);

            setApprovedTags(approvedSnap.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            })));

            // Load rejected tags
            const rejectedQuery = query(
                collection(db, 'settings', 'tag_schema', 'rejected_tags'),
                orderBy('rejectedAt', 'desc'),
                limit(100)
            );
            const rejectedSnap = await getDocs(rejectedQuery);

            setRejectedTags(rejectedSnap.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            })));

            // Calculate stats
            const categoryCounts = {
                category: 0,
                dietary: 0,
                format: 0,
                brand: 0,
                attribute: 0
            };

            approvedSnap.docs.forEach(docSnap => {
                const cat = docSnap.data().category || 'attribute';
                categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
            });

            setStats({
                total: approvedSnap.size,
                rejected: rejectedSnap.size,
                ...categoryCounts
            });

        } catch (error) {
            console.error('Error loading tag schema:', error);
            setToast({ content: 'Error loading tag schema', error: true });
        }
        setLoading(false);
    };

    // Filter approved tags
    const filteredTags = useMemo(() => {
        let tags = approvedTags;

        if (categoryFilter !== 'all') {
            tags = tags.filter(t => t.category === categoryFilter);
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            tags = tags.filter(t => t.tag.toLowerCase().includes(query));
        }

        return tags;
    }, [approvedTags, categoryFilter, searchQuery]);

    // Add new tag
    const handleAddTag = async () => {
        if (!newTag.tag.trim()) {
            setToast({ content: 'Please enter a tag', error: true });
            return;
        }

        const normalized = newTag.tag.toLowerCase().trim().replace(/\s+/g, '-');

        try {
            const docRef = doc(db, 'settings', 'tag_schema', 'approved_tags', normalized);
            await setDoc(docRef, {
                tag: normalized,
                category: newTag.category,
                approvedAt: new Date(),
                approvedBy: 'manual'
            });

            setApprovedTags(prev => [{
                id: normalized,
                tag: normalized,
                category: newTag.category,
                approvedAt: { toDate: () => new Date() },
                approvedBy: 'manual'
            }, ...prev]);

            setToast({ content: `Added tag: ${normalized}` });
            setAddModalOpen(false);
            setNewTag({ tag: '', category: 'attribute' });

        } catch (error) {
            console.error('Error adding tag:', error);
            setToast({ content: 'Error adding tag', error: true });
        }
    };

    // Remove tag
    const handleRemoveTag = async (tagId) => {
        try {
            const docRef = doc(db, 'settings', 'tag_schema', 'approved_tags', tagId);
            await deleteDoc(docRef);
            setApprovedTags(prev => prev.filter(t => t.id !== tagId));
            setToast({ content: `Removed tag: ${tagId}` });
        } catch (error) {
            console.error('Error removing tag:', error);
            setToast({ content: 'Error removing tag', error: true });
        }
    };

    // Restore rejected tag
    const handleRestoreTag = async (tag) => {
        try {
            // Add to approved
            const approvedRef = doc(db, 'settings', 'tag_schema', 'approved_tags', tag.tag);
            await setDoc(approvedRef, {
                tag: tag.tag,
                category: 'attribute',
                approvedAt: new Date(),
                approvedBy: 'restored'
            });

            // Remove from rejected
            const rejectedRef = doc(db, 'settings', 'tag_schema', 'rejected_tags', tag.id);
            await deleteDoc(rejectedRef);

            setRejectedTags(prev => prev.filter(t => t.id !== tag.id));
            loadData();

            setToast({ content: `Restored tag: ${tag.tag}` });
        } catch (error) {
            console.error('Error restoring tag:', error);
            setToast({ content: 'Error restoring tag', error: true });
        }
    };

    // Export schema
    const handleExport = () => {
        const schema = {
            approvedTags: approvedTags.map(t => ({
                tag: t.tag,
                category: t.category
            })),
            exportedAt: new Date().toISOString()
        };

        const blob = new Blob([JSON.stringify(schema, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tag-schema-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);

        setToast({ content: 'Schema exported' });
    };

    const tabs = [
        { id: 'approved', content: `Approved (${stats?.total || 0})`, panelID: 'approved-panel' },
        { id: 'rejected', content: `Rejected (${stats?.rejected || 0})`, panelID: 'rejected-panel' }
    ];

    if (loading) {
        return (
            <div className="tag-schema-loading">
                <Spinner size="large" />
                <p>Loading tag schema...</p>
            </div>
        );
    }

    return (
        <div className="tag-schema-manager">
            <div className="page-header">
                <div className="header-content">
                    <h1>Tag Schema Manager</h1>
                    <p className="subtitle">Manage your product tag taxonomy for AI enrichment</p>
                </div>
                <Stack spacing="tight">
                    <Button onClick={handleExport}>Export Schema</Button>
                    <Button primary onClick={() => setAddModalOpen(true)}>Add Tag</Button>
                </Stack>
            </div>

            {/* Stats Banner */}
            {stats && (
                <div className="stats-banner">
                    <div className="stat-item">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total Tags</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.category}</span>
                        <span className="stat-label">Categories</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.dietary}</span>
                        <span className="stat-label">Dietary</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.format}</span>
                        <span className="stat-label">Formats</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{stats.attribute}</span>
                        <span className="stat-label">Attributes</span>
                    </div>
                </div>
            )}

            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                {/* Approved Tags Tab */}
                {selectedTab === 0 && (
                    <Card sectioned>
                        <Stack distribution="equalSpacing" alignment="center">
                            <Stack spacing="tight">
                                <TextField
                                    label=""
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder="Search tags..."
                                />
                                <Select
                                    label=""
                                    options={CATEGORIES}
                                    value={categoryFilter}
                                    onChange={setCategoryFilter}
                                />
                            </Stack>
                            <span className="result-count">{filteredTags.length} tags</span>
                        </Stack>

                        <div className="tag-grid">
                            {filteredTags.map(tag => (
                                <div key={tag.id} className="tag-item">
                                    <Badge status={getCategoryStatus(tag.category)}>
                                        {tag.tag}
                                    </Badge>
                                    <span className="tag-category">{tag.category}</span>
                                    <button
                                        className="tag-remove"
                                        onClick={() => handleRemoveTag(tag.id)}
                                        title="Remove tag"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>

                        {filteredTags.length === 0 && (
                            <div className="empty-state">
                                <p>No tags found matching your criteria.</p>
                            </div>
                        )}
                    </Card>
                )}

                {/* Rejected Tags Tab */}
                {selectedTab === 1 && (
                    <Card sectioned>
                        <Banner status="info" title="Rejected Tags">
                            <p>These tags were rejected during AI enrichment review. You can restore them if they should be added to the schema.</p>
                        </Banner>

                        {rejectedTags.length === 0 ? (
                            <div className="empty-state">
                                <p>No rejected tags.</p>
                            </div>
                        ) : (
                            <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text']}
                                headings={['Tag', 'Reason', 'Rejected', 'Actions']}
                                rows={rejectedTags.map(tag => [
                                    tag.tag,
                                    tag.reason || '-',
                                    tag.rejectedAt?.toDate?.()?.toLocaleDateString() || 'Unknown',
                                    <Button size="slim" onClick={() => handleRestoreTag(tag)}>
                                        Restore
                                    </Button>
                                ])}
                            />
                        )}
                    </Card>
                )}
            </Tabs>

            {/* Add Tag Modal */}
            <Modal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                title="Add New Tag"
                primaryAction={{
                    content: 'Add Tag',
                    onAction: handleAddTag
                }}
                secondaryActions={[
                    { content: 'Cancel', onAction: () => setAddModalOpen(false) }
                ]}
            >
                <Modal.Section>
                    <Stack vertical>
                        <TextField
                            label="Tag Name"
                            value={newTag.tag}
                            onChange={(value) => setNewTag({ ...newTag, tag: value })}
                            placeholder="e.g., gluten-free"
                            helpText="Will be normalized to lowercase with hyphens"
                        />
                        <Select
                            label="Category"
                            options={CATEGORIES.filter(c => c.value !== 'all')}
                            value={newTag.category}
                            onChange={(value) => setNewTag({ ...newTag, category: value })}
                        />
                    </Stack>
                </Modal.Section>
            </Modal>

            {/* Toast */}
            {toast && (
                <Toast
                    content={toast.content}
                    error={toast.error}
                    onDismiss={() => setToast(null)}
                />
            )}
        </div>
    );
};

// Helper function
function getCategoryStatus(category) {
    switch (category) {
        case 'category': return 'info';
        case 'dietary': return 'success';
        case 'format': return 'attention';
        case 'brand': return 'warning';
        default: return undefined;
    }
}

export default TagSchemaManager;
